// Shared invite panel for creating staff accounts from the dashboard. Used by the Team page
// (role="command") and the Doctors page (role="doctor"). Submits to POST /v1/command/staff, which
// creates the Cognito user (Cognito emails the invite) + assigns the group. Inline panel (matches
// the edit-in-place pattern used by DoctorActions) — no modal primitive in this app.
import { useState } from "react";
import { ApiError } from "@forcebase-ai/api-client";
import { useApi } from "../api";

const DOMAIN_HINT: Record<"command" | "doctor", string> = {
  command: "forcebase.ai",
  doctor: "drstack.ai or thunderbirdlabs.ai",
};

export function InviteStaff({
  role,
  onInvited,
  onClose,
}: {
  role: "command" | "doctor";
  onInvited: () => void;
  onClose: () => void;
}) {
  const api = useApi();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.inviteStaff({ role, email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
      setFlash(
        res.status === "exists"
          ? `${res.email} already had an account — group re-confirmed.`
          : `Invite emailed to ${res.email}.`,
      );
      setFirstName("");
      setLastName("");
      setEmail("");
      onInvited();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="invite-panel" onSubmit={submit}>
      <div className="invite-panel__row">
        <label>
          First name
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={80} />
        </label>
        <label>
          Last name
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={80} />
        </label>
      </div>
      <label className="invite-panel__full">
        Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={role === "command" ? "name@forcebase.ai" : "name@drstack.ai / thunderbirdlabs.ai"}
          required
        />
        <span className="invite-panel__hint">Must be a {DOMAIN_HINT[role]} address.</span>
      </label>
      {error && <p className="invite-panel__error" role="alert">{error}</p>}
      {flash && <p className="invite-panel__flash" role="status">{flash}</p>}
      <div className="invite-panel__actions">
        <button type="button" className="pbtn" onClick={onClose} disabled={busy}>Close</button>
        <button type="submit" className="pbtn pbtn--primary" disabled={busy}>
          {busy ? "Sending…" : "Send invite"}
        </button>
      </div>
    </form>
  );
}
